// src/pages/UserDashboard.js - Complete Fixed Version
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase.config';
import ServiceDirectory from '../services/ServiceDirectory';
import FinancialDashboard from '../components/financial/FinancialDashboard';
import AnnouncementBoard from '../components/announcements/AnnouncementBoard';
import CommunityRules from '../components/rules/CommunityRules';
import ComplaintManagement from '../components/complaints/ComplaintManagement';
import HousekeepingManagement from '../components/housekeeping/HousekeepingManagement';
import './UserDashboard.css';

function UserDashboard({ user, userData, onLogout, isAdminUser, onSwitchToAdmin  }) {
  // Existing state
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [activeView, setActiveView] = useState('dashboard');
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // New enhanced features state
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [refreshing, setRefreshing] = useState(false); // Added missing state
  const notificationRef = useRef(null);

  useEffect(() => {
    fetchUserData();
    setupRealtimeSubscriptions();
    loadNotifications();

    if (darkMode) {
      document.body.classList.add('dark-mode');
    }

    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      supabase.removeAllChannels();
    };
  }, [userData, darkMode]);

  // Real-time subscriptions
  const setupRealtimeSubscriptions = () => {
    if (!userData?.id) return;

    const announcementChannel = supabase
      .channel('announcements')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'announcements' },
        (payload) => {
          handleNewAnnouncement(payload.new);
        }
      )
      .subscribe();

    const paymentChannel = supabase
      .channel('payments')
      .on('postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'payments',
          filter: `resident_id=eq.${userData.id}`
        },
        () => {
          fetchPaymentData();
        }
      )
      .subscribe();

    const meetingChannel = supabase
      .channel('meetings')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'meetings' },
        () => {
          fetchMeetings();
        }
      )
      .subscribe();
  };

  // Handle new announcement notification
  const handleNewAnnouncement = (announcement) => {
    const newNotification = {
      id: Date.now(),
      type: 'announcement',
      title: 'New Announcement',
      message: announcement.title,
      timestamp: new Date().toISOString(),
      read: false
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    
    if (Notification.permission === 'granted') {
      new Notification('New Announcement', {
        body: announcement.title,
        icon: '/logo192.png'
      });
    }
  };

  // Load notifications
  const loadNotifications = async () => {
    const mockNotifications = [
      {
        id: 1,
        type: 'payment',
        title: 'Payment Reminder',
        message: 'Your maintenance fee is due in 3 days',
        timestamp: new Date().toISOString(),
        read: false
      },
      {
        id: 2,
        type: 'meeting',
        title: 'Meeting Tomorrow',
        message: 'Monthly society meeting at 10 AM',
        timestamp: new Date().toISOString(),
        read: false
      }
    ];
    setNotifications(mockNotifications);
  };

  // Quick RSVP for meetings - Added missing function
  const handleQuickRSVP = async (meetingId, status) => {
    try {
      const { error } = await supabase
        .from('meeting_attendance')
        .upsert({
          meeting_id: meetingId,
          resident_id: userData.id,
          status: status,
          updated_at: new Date().toISOString()
        });

      if (!error) {
        alert(`RSVP ${status === 'attending' ? 'confirmed' : 'declined'}!`);
        fetchMeetings();
      }
    } catch (error) {
      console.error('RSVP error:', error);
      alert('Failed to update RSVP status');
    }
  };

  // Pull to refresh functionality
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUserData();
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Fetch payment data
  const fetchPaymentData = async () => {
    if (!userData?.id) return;
    
    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      
      const { data: currentPayment } = await supabase
        .from('payments')
        .select('*')
        .eq('resident_id', userData.id)
        .eq('payment_month', currentMonth)
        .eq('payment_year', currentYear)
        .single();
      
      setPaymentStatus(currentPayment);

      const { data: history } = await supabase
        .from('payments')
        .select('*')
        .eq('resident_id', userData.id)
        .order('payment_year', { ascending: false })
        .order('payment_month', { ascending: false })
        .limit(6);
      
      setPaymentHistory(history || []);
    } catch (error) {
      console.error('Error fetching payment data:', error);
    }
  };

  // Fetch meetings
  const fetchMeetings = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: meetingsData } = await supabase
        .from('meetings')
        .select('*')
        .gte('meeting_date', today.toISOString().split('T')[0])
        .eq('status', 'scheduled')
        .order('meeting_date', { ascending: true })
        .limit(3);
      
      if (meetingsData) {
        setUpcomingMeetings(meetingsData);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  // Mark notification as read
  const markNotificationRead = (id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const fetchUserData = async () => {
    if (!userData || !userData.id) return;

    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      
      // Fetch action items
      const { data: userActionItems } = await supabase
        .from('action_items')
        .select(`
          *,
          meetings (
            title,
            meeting_date
          )
        `)
        .or(`assigned_to_id.eq.${userData.id},assigned_to.eq.all`)
        .order('created_at', { ascending: false });

      if (userActionItems) {
        setActionItems(userActionItems);
      }

      // Fetch announcements
      const { data: announcementData } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(3);

      if (announcementData) {
        const { data: readData } = await supabase
          .from('announcement_reads')
          .select('announcement_id')
          .eq('resident_id', userData.id);

        const readAnnouncementIds = readData?.map(r => r.announcement_id) || [];
        
        const processedAnnouncements = announcementData.map(announcement => ({
          ...announcement,
          isRead: readAnnouncementIds.includes(announcement.id)
        }));

        setRecentAnnouncements(processedAnnouncements);
      }

      await fetchPaymentData();
      await fetchMeetings();

      // Fetch events
      const { data: eventData } = await supabase
        .from('events')
        .select('*')
        .gte('event_date', new Date().toISOString())
        .order('event_date', { ascending: true })
        .limit(5);
      
      setEvents(eventData || []);

      // Fetch reminders
      const { data: reminderData } = await supabase
        .from('reminders')
        .select('*')
        .eq('user_id', userData.id)
        .eq('is_completed', false)
        .order('due_date', { ascending: true });
      
      setReminders(reminderData || []);

      // Fetch maintenance tasks
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: allTasks } = await supabase
        .from('maintenance_tasks')
        .select('*')
        .order('scheduled_date', { ascending: true });

      if (allTasks && allTasks.length > 0) {
        const upcomingTasks = allTasks.filter(task => {
          const taskDate = new Date(task.scheduled_date);
          return taskDate >= today && (task.status === 'scheduled' || task.status === 'in_progress');
        });
        
        if (upcomingTasks.length > 0) {
          const { data: categoriesData } = await supabase
            .from('maintenance_categories')
            .select('*');
          
          const { data: vendorsData } = await supabase
            .from('service_vendors')
            .select('*');
          
          const processedTasks = upcomingTasks.map(task => {
            const category = categoriesData?.find(cat => cat.id === task.category_id);
            const vendor = vendorsData?.find(v => v.id === task.assigned_vendor_id);
            
            return {
              ...task,
              maintenance_categories: category || null,
              service_vendors: vendor || null
            };
          });
          
          setMaintenanceTasks(processedTasks.slice(0, 5));
        } else {
          setMaintenanceTasks([]);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setLoading(false);
    }
  };

  const downloadReceipt = async (payment) => {
    const receiptContent = `
MAINTENANCE RECEIPT
=====================================
Receipt No: ${payment.receipt_number || 'N/A'}
Date: ${new Date(payment.payment_date).toLocaleDateString()}

Resident: ${userData.name}
Flat: ${userData.flat_number}

Month: ${payment.payment_month}/${payment.payment_year}
Amount: ₹${payment.amount}

Payment Method: ${payment.payment_method?.toUpperCase()}
Transaction Ref: ${payment.transaction_ref || 'N/A'}
=====================================
    `;
    
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receipt_${payment.receipt_number || 'receipt'}.txt`;
    a.click();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  function TaskItemCard({ item, userData, isMyTask, onRefresh }) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [completionNotes, setCompletionNotes] = useState('');
    const [showNotesInput, setShowNotesInput] = useState(false);

    const handleStatusUpdate = async (newStatus) => {
      setIsUpdating(true);
      try {
        const updateData = {
          status: newStatus,
          updated_at: new Date().toISOString()
        };

        if (newStatus === 'completed') {
          updateData.completed_at = new Date().toISOString();
          updateData.completed_by = userData.id;
          if (completionNotes) {
            updateData.completion_notes = completionNotes;
          }
        }

        const { error } = await supabase
          .from('action_items')
          .update(updateData)
          .eq('id', item.id);

        if (error) throw error;

        alert(`Task marked as ${newStatus}`);
        setCompletionNotes('');
        setShowNotesInput(false);
        onRefresh();
      } catch (error) {
        console.error('Error updating task:', error);
        alert('Failed to update task status');
      } finally {
        setIsUpdating(false);
      }
    };

    const getStatusColor = (status) => {
      switch(status) {
        case 'completed': return '#28a745';
        case 'in_progress': return '#17a2b8';
        case 'pending': return '#ffc107';
        default: return '#6c757d';
      }
    };

    const getPriorityColor = (priority) => {
      switch(priority) {
        case 'high': return '#dc3545';
        case 'medium': return '#ffc107';
        case 'low': return '#28a745';
        default: return '#6c757d';
      }
    };

    return (
      <div className="task-item-card">
        <div className="task-description">
          {item.description}
        </div>
        
        <div className="task-meta">
          <div><strong>From Meeting:</strong> {item.meetings?.title || 'General Meeting'}</div>
          <div><strong>Assigned to:</strong> {item.assigned_to || 'All Residents'}</div>
          {item.due_date && (
            <div><strong>Due:</strong> {new Date(item.due_date).toLocaleDateString()}</div>
          )}
        </div>

        <div className="task-badges">
          <span className="status-badge" style={{ background: getStatusColor(item.status) }}>
            {item.status?.replace('_', ' ').toUpperCase()}
          </span>
          
          <span className="priority-badge" style={{ background: getPriorityColor(item.priority) }}>
            {item.priority?.toUpperCase()} PRIORITY
          </span>
        </div>

        {isMyTask && item.status !== 'completed' && (
          <div className="task-actions">
            {item.status === 'pending' && (
              <button
                onClick={() => handleStatusUpdate('in_progress')}
                disabled={isUpdating}
                className="btn-start-progress"
              >
                {isUpdating ? 'Updating...' : '▶️ Start Progress'}
              </button>
            )}
            
            {item.status === 'in_progress' && (
              <>
                {!showNotesInput ? (
                  <button
                    onClick={() => setShowNotesInput(true)}
                    className="btn-mark-complete"
                  >
                    ✅ Mark Complete
                  </button>
                ) : (
                  <div className="completion-input">
                    <input
                      type="text"
                      placeholder="Add completion notes (optional)"
                      value={completionNotes}
                      onChange={(e) => setCompletionNotes(e.target.value)}
                    />
                    <button
                      onClick={() => handleStatusUpdate('completed')}
                      disabled={isUpdating}
                      className="btn-confirm"
                    >
                      {isUpdating ? '...' : '✔'}
                    </button>
                    <button
                      onClick={() => {
                        setShowNotesInput(false);
                        setCompletionNotes('');
                      }}
                      className="btn-cancel"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {item.status === 'completed' && item.completion_notes && (
          <div className="completion-notes">
            <strong>Completion Notes:</strong> {item.completion_notes}
          </div>
        )}
      </div>
    );
  }

  // Handle special views
  if (activeView === 'housekeeping') {
    return <HousekeepingManagement userData={userData} onBack={() => setActiveView('dashboard')} />;
  }
  if (activeView === 'financial') {
    return <FinancialDashboard userData={userData} onBack={() => setActiveView('dashboard')} />;
  }
  if (activeView === 'complaints') {
    return <ComplaintManagement userData={userData} onBack={() => setActiveView('dashboard')} />;
  }
  if (activeView === 'rules') {
    return <CommunityRules userData={userData} onBack={() => setActiveView('dashboard')} />;
  }
  if (activeView === 'announcements') {
    return <AnnouncementBoard userData={userData} onBack={() => setActiveView('dashboard')} />;
  }
  if (activeView === 'services') {
    return <ServiceDirectory userData={userData} onBack={() => setActiveView('dashboard')} />;
  }

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      {/* Pull to refresh indicator */}
      {refreshing && (
        <div className="refresh-indicator">
          <div className="spinner-small"></div>
          Refreshing...
        </div>
      )}

      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          {(userData?.role === 'admin' || userData?.role === 'superadmin') && (
            <>
              <button
                className="menu-item active"
                onClick={() => {
                  // Stay on user dashboard
                  closeMobileMenu();
                }}
              >
                <span className="menu-icon">👤</span>
                <span className="menu-label">My Dashboard</span>
              </button>
              <button
                className="menu-item"
                onClick={() => {
                  // Switch to admin panel
                  if (onSwitchToAdmin) {
                    onSwitchToAdmin();
                  }
                  closeMobileMenu();
                }}
              >
                <span className="menu-icon">🛡️</span>
                <span className="menu-label">Admin Panel</span>
              </button>
              <div className="sidebar-divider"></div>
            </>
          )}
          
          <button
            className={`menu-item ${activeView === 'dashboard' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('dashboard');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📊</span>
            <span className="menu-label">Overview</span>
          </button>
          <button
            className={`menu-item ${activeView === 'financial' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('financial');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">💰</span>
            <span className="menu-label">Financial</span>
          </button>
          <button
            className={`menu-item ${activeView === 'complaints' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('complaints');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📝</span>
            <span className="menu-label">Complaints</span>
          </button>
          <button
            className={`menu-item ${activeView === 'announcements' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('announcements');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📢</span>
            <span className="menu-label">Announcements</span>
          </button>
          <button
            className={`menu-item ${activeView === 'housekeeping' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('housekeeping');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">🧹</span>
            <span className="menu-label">Housekeeping</span>
          </button>
          <button
            className={`menu-item ${activeView === 'services' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('services');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📞</span>
            <span className="menu-label">Services</span>
          </button>
          <button
            className={`menu-item ${activeView === 'rules' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('rules');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">Rules</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          <button className="logout-button" onClick={onLogout}>
            <span className="menu-icon">🚪</span>
            <span className="menu-label">Logout</span>
          </button>
        </div>
      </aside>
      
      <main className="dashboard-main">
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          
          <div className="header-search">
            <input
              type="text"
              placeholder="Search payments, meetings, announcements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <div className="header-actions">
            <div className="notification-wrapper" ref={notificationRef}>
              <button 
                className="notification-btn"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <span>🔔</span>
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="notification-badge">
                    {notifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>
              
              {showNotifications && (
                <div className="notifications-dropdown">
                  <div className="dropdown-header">
                    <h4>Notifications</h4>
                    <button onClick={() => setShowNotifications(false)}>✕</button>
                  </div>
                  <div className="notifications-list">
                    {notifications.length > 0 ? (
                      notifications.map(notif => (
                        <div 
                          key={notif.id}
                          className={`notification-item ${!notif.read ? 'unread' : ''}`}
                          onClick={() => markNotificationRead(notif.id)}
                        >
                          <div className="notif-icon">
                            {notif.type === 'payment' ? '💰' : 
                             notif.type === 'meeting' ? '📅' : '📢'}
                          </div>
                          <div className="notif-content">
                            <strong>{notif.title}</strong>
                            <p>{notif.message}</p>
                            <small>{new Date(notif.timestamp).toLocaleString()}</small>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-notifications">No new notifications</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        
        <div className="dashboard-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading dashboard...</p>
            </div>
          ) : (
            <div className="dashboard-grid">
              {/* Payment Status Card */}
              <div className="dashboard-card payment-status-card">
                <h3>💳 Payment Status</h3>
                {paymentStatus ? (
                  <div>
                    <p><strong>Current Month:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                    <p><strong>Status:</strong> 
                      <span className="status-badge paid">PAID</span>
                    </p>
                    <p><strong>Amount:</strong> ₹{paymentStatus.amount}</p>
                    <p><strong>Paid on:</strong> {new Date(paymentStatus.payment_date).toLocaleDateString()}</p>
                    <button onClick={() => downloadReceipt(paymentStatus)} className="download-btn" style={{
    marginTop: '12px',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  }}
  onMouseEnter={(e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 4px 8px rgba(102, 126, 234, 0.3)';
  }}
  onMouseLeave={(e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
  }}>
                      📄 Download Receipt
                    </button>
                  </div>
                ) : (
                  <div>
                    <p><strong>Current Month:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                    <p><strong>Status:</strong> 
                      <span className="status-badge pending">PENDING</span>
                    </p>
                    <p><strong>Amount Due:</strong> ₹2000</p>
                    <p className="help-text">Please pay to the maintenance in-charge</p>
                  </div>
                )}
              </div>

              {/* Enhanced Meeting Card with RSVP */}
              <div className="dashboard-card meeting-card">
                <div className="card-header">
                  <h3>📅 Upcoming Meetings</h3>
                  {upcomingMeetings.length > 0 && (
                    <span className="count-badge">{upcomingMeetings.length} Scheduled</span>
                  )}
                </div>
                {upcomingMeetings.length > 0 ? (
                  <div className="meetings-list">
                    {upcomingMeetings.map(meeting => (
                      <div key={meeting.id} className="meeting-item enhanced">
                        <div className="meeting-content">
                          <div className="meeting-title">{meeting.title}</div>
                          <div className="meeting-details">
                            <span>📅 {new Date(meeting.meeting_date).toLocaleDateString()}</span>
                            {meeting.meeting_time && <span>⏰ {formatTime(meeting.meeting_time)}</span>}
                            <span>📍 {meeting.venue}</span>
                          </div>
                          {meeting.agenda && (
                            <div className="meeting-agenda">
                              <strong>Agenda:</strong><br/>
                              {meeting.agenda}
                            </div>
                          )}
                        </div>
                        <div className="meeting-actions">
                          <button 
                            className="rsvp-btn attending"
                            onClick={() => handleQuickRSVP(meeting.id, 'attending')}
                          >
                            ✓ Attending
                          </button>
                          <button 
                            className="rsvp-btn not-attending"
                            onClick={() => handleQuickRSVP(meeting.id, 'not_attending')}
                          >
                            ✗ Can't Attend
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No upcoming meetings scheduled</p>
                    <small>You'll be notified when a meeting is scheduled</small>
                  </div>
                )}
              </div>

              <div className="dashboard-card maintenance-card">
                <div className="card-header">
                  <h3>🔧 Upcoming Maintenance</h3>
                  <span className="status-label">Scheduled</span>
                </div>
                {maintenanceTasks.length > 0 ? (
                  <div className="maintenance-list">
                    {maintenanceTasks.map(task => (
                      <div key={task.id} className="maintenance-item">
                        <div className="maintenance-header">
                          <div className="maintenance-title">
                            {task.maintenance_categories?.icon} {task.title}
                          </div>
                          <span className={`priority-badge priority-${task.priority}`}>
                            {task.priority?.toUpperCase()}
                          </span>
                        </div>
                        <div className="maintenance-details">
                          <span><strong>Category:</strong> {task.maintenance_categories?.name || 'General'}</span>
                          {task.description && (
                            <span>{task.description.substring(0, 60)}...</span>
                          )}
                          <div className="maintenance-schedule">
                            <span>📅 {formatDate(task.scheduled_date)}</span>
                            {task.scheduled_time && <span>⏰ {formatTime(task.scheduled_time)}</span>}
                            {task.duration_hours && <span>⏱️ {task.duration_hours}h</span>}
                          </div>
                          {task.service_vendors && (
                            <div className="vendor-info">
                              <strong>Vendor:</strong> {task.service_vendors.name}
                              {task.service_vendors.phone && <span> 📞 {task.service_vendors.phone}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="alert-message">
                      ⚠️ Please plan accordingly. Some services may be temporarily unavailable during maintenance.
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No scheduled maintenance</p>
                    <small>Check back later for updates</small>
                  </div>
                )}
              </div>

              <div className="dashboard-card action-card">
                <div className="card-header">
                  <h3>📋 Meeting Action Items</h3>
                  {actionItems.length > 0 && (
                    <span className="count-badge">
                      {actionItems.filter(a => a.status === 'pending').length} Pending
                    </span>
                  )}
                </div>
                {actionItems.length > 0 ? (
                  <div className="action-items-list">
                    {actionItems.filter(item => 
                      item.assigned_to_id === userData.id || 
                      item.assigned_to === `${userData.flat_number} - ${userData.name}`
                    ).length > 0 && (
                      <div className="task-section">
                        <h4 className="task-section-header my-tasks">📌 My Tasks</h4>
                        {actionItems.filter(item => 
                          item.assigned_to_id === userData.id || 
                          item.assigned_to === `${userData.flat_number} - ${userData.name}`
                        ).map(item => (
                          <TaskItemCard 
                            key={item.id} 
                            item={item} 
                            userData={userData}
                            isMyTask={true}
                            onRefresh={fetchUserData}
                          />
                        ))}
                      </div>
                    )}

                    {actionItems.filter(item => 
                      item.assigned_to === 'All' || 
                      item.assigned_to === 'all' ||
                      item.assigned_to === 'All Residents'
                    ).length > 0 && (
                      <div className="task-section">
                        <h4 className="task-section-header all-tasks">👥 Tasks for All Residents</h4>
                        {actionItems.filter(item => 
                          item.assigned_to === 'All' || 
                          item.assigned_to === 'all' ||
                          item.assigned_to === 'All Residents'
                        ).map(item => (
                          <TaskItemCard 
                            key={item.id} 
                            item={item} 
                            userData={userData}
                            isMyTask={true}
                            onRefresh={fetchUserData}
                          />
                        ))}
                      </div>
                    )}

                    {actionItems.filter(item => 
                      !(item.assigned_to_id === userData.id || 
                        item.assigned_to === `${userData.flat_number} - ${userData.name}` ||
                        item.assigned_to === 'All' || 
                        item.assigned_to === 'all' ||
                        item.assigned_to === 'All Residents')
                    ).length > 0 && (
                      <div className="task-section">
                        <h4 className="task-section-header other-tasks">👁️ Other Tasks (View Only)</h4>
                        {actionItems.filter(item => 
                          !(item.assigned_to_id === userData.id || 
                            item.assigned_to === `${userData.flat_number} - ${userData.name}` ||
                            item.assigned_to === 'All' || 
                            item.assigned_to === 'all' ||
                            item.assigned_to === 'All Residents')
                        ).map(item => (
                          <TaskItemCard 
                            key={item.id} 
                            item={item} 
                            userData={userData}
                            isMyTask={false}
                            onRefresh={fetchUserData}
                          />
                        ))}
                      </div>
                    )}
                    
                    <div className="info-message">
                      💡 Action items help track decisions made in meetings and ensure follow-through on commitments.
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No action items from meetings</p>
                    <small>Action items from meetings will appear here</small>
                  </div>
                )}
              </div>

              <div className="dashboard-card">
                <h3>📜 Payment History</h3>
                {paymentHistory && paymentHistory.length > 0 ? (
                  <div className="payment-history-list">
                    {paymentHistory.map(payment => (
                      <div key={payment.id} className="payment-history-item">
                        <div className="payment-info">
                          <strong>{payment.payment_month}/{payment.payment_year}</strong>
                          <small>₹{payment.amount} - {new Date(payment.payment_date).toLocaleDateString()}</small>
                        </div>
                        <button
                          onClick={() => downloadReceipt(payment)}
                          className="receipt-btn"
                        >
                          Receipt
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No payment history available</p>
                )}
              </div>

              <div className="dashboard-card">
                <div className="card-header">
                  <h3>📢 Recent Announcements</h3>
                  <button onClick={() => setActiveView('announcements')} className="view-all-btn">
                    View All
                  </button>
                </div>
                {recentAnnouncements && recentAnnouncements.length > 0 ? (
                  <div>
                    {recentAnnouncements.map(announcement => (
                      <div 
                        key={announcement.id} 
                        className={`announcement-item ${announcement.isRead ? 'read' : 'unread'}`}
                        onClick={() => setActiveView('announcements')}
                      >
                        <div className="announcement-header">
                          {!announcement.isRead && <span className="new-badge">NEW</span>}
                          <strong>{announcement.title}</strong>
                        </div>
                        <div className="announcement-content">
                          {announcement.content?.substring(0, 100) || announcement.message?.substring(0, 100)}
                          {(announcement.content?.length > 100 || announcement.message?.length > 100) && '...'}
                        </div>
                        <div className="announcement-footer">
                          <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                          <span className="category-badge">
                            {announcement.category || 'general'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div>🔭</div>
                    <p>No announcements</p>
                  </div>
                )}
              </div>

              <div className="dashboard-card">
                <h3>🔗 Quick Links</h3>
                <div className="quick-links-grid">
                  <button onClick={() => setActiveView('complaints')} className="quick-link-btn complaint">
                    📝 Submit Complaint
                  </button>
                  <button onClick={() => setActiveView('housekeeping')} className="quick-link-btn housekeeping">
                    🧹 Housekeeping
                  </button>
                  <button onClick={() => {
                    setActiveView('services');
                    sessionStorage.setItem('showEmergency', 'true');
                  }} className="quick-link-btn emergency">
                    🚨 Emergency Contacts
                  </button>
                  <button onClick={() => setActiveView('rules')} className="quick-link-btn rules">
                    📋 Community Rules
                  </button>
                  <button onClick={() => setActiveView('financial')} className="quick-link-btn financial">
                    💰 Financial Overview
                  </button>
                  <button onClick={() => setActiveView('services')} className="quick-link-btn services">
                    📞 Service Directory
                  </button>
                </div>
              </div>
                  
              <div className="dashboard-card">
                <h3>⏰ Personal Reminders</h3>
                {reminders.length > 0 ? (
                  <ul className="reminders-list">
                    {reminders.map(reminder => (
                      <li key={reminder.id} className="reminder-item">
                        <input type="checkbox" />
                        <div>
                          <strong>{reminder.title}</strong>
                          <small>Due: {new Date(reminder.due_date).toLocaleDateString()}</small>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No active reminders</p>
                )}
              </div>
            </div>
          )}
        </div>
         <div className="fab-container">
          <button className="fab" onClick={() => setActiveView('complaints')}>
            <span>+</span>
          </button>
          <div className="fab-menu">
            <button onClick={() => setActiveView('complaints')}>📝 Complaint</button>
            <button onClick={() => setActiveView('housekeeping')}>🧹 Housekeeping</button>
            <button onClick={() => window.location.href = 'tel:emergency'}>🚨 Emergency</button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default UserDashboard;